/** Consultas e ações da equipe e da distribuição, usadas por várias telas. */
import type {
  AlertaDto,
  AtribuicaoDto,
  ConfigDistribuicao,
  StatusPresenca,
  UsuarioDetalhe,
  UsuarioResumo,
} from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ErroApi } from "./api";

export const COR_PRESENCA: Record<StatusPresenca, string> = {
  disponivel: "bg-success-500",
  ocupada: "bg-warning-500",
  pausa: "bg-gold-400",
  offline: "bg-neutral-400",
};

const erro = (e: unknown, padrao: string) => toast.error(e instanceof ErroApi ? e.message : padrao);

export const useUsuariosResumo = () =>
  useQuery({
    queryKey: ["usuarios", "resumo"],
    queryFn: () => api.get<UsuarioResumo[]>("/usuarios/resumo"),
    staleTime: 30_000,
  });

export const useUsuarios = (incluirInativos = false) =>
  useQuery({
    queryKey: ["usuarios", "detalhe", incluirInativos],
    queryFn: () => api.get<UsuarioDetalhe[]>(`/usuarios${incluirInativos ? "?inativos=1" : ""}`),
  });

export const useAlertas = (habilitado: boolean) =>
  useQuery({
    queryKey: ["alertas"],
    queryFn: () => api.get<AlertaDto[]>("/equipe/alertas"),
    enabled: habilitado,
    refetchInterval: 60_000,
  });

export const useConfigDistribuicao = (empresaId: string | undefined) =>
  useQuery({
    queryKey: ["distribuicao", empresaId],
    queryFn: () => api.get<ConfigDistribuicao>(`/equipe/distribuicao?empresaId=${empresaId}`),
    enabled: !!empresaId,
  });

export const useAtribuicoes = (conversaId: string) =>
  useQuery({
    queryKey: ["conversa", conversaId, "atribuicoes"],
    queryFn: () => api.get<AtribuicaoDto[]>(`/conversas/${conversaId}/atribuicoes`),
  });

/** Invalida tudo que muda quando uma conversa troca de mãos. */
function useInvalidarOperacao() {
  const qc = useQueryClient();
  return (conversaId?: string) => {
    void qc.invalidateQueries({ queryKey: ["conversas"] });
    void qc.invalidateQueries({ queryKey: ["usuarios"] });
    void qc.invalidateQueries({ queryKey: ["alertas"] });
    if (conversaId) void qc.invalidateQueries({ queryKey: ["conversa", conversaId] });
  };
}

export function useAcoesConversa() {
  const invalidar = useInvalidarOperacao();
  const assumir = useMutation({
    mutationFn: (id: string) => api.post(`/conversas/${id}/assumir`),
    onSuccess: (_r, id) => {
      toast.success("Atendimento assumido!");
      invalidar(id);
    },
    onError: (e, id) => {
      erro(e, "Não foi possível assumir.");
      invalidar(id);
    },
  });
  const transferir = useMutation({
    mutationFn: (v: { id: string; usuarioId: string; nome: string; motivo?: string }) =>
      api.post(`/conversas/${v.id}/transferir`, {
        usuarioId: v.usuarioId,
        motivo: v.motivo || null,
      }),
    onSuccess: (_r, v) => {
      toast.success(`Transferida para ${v.nome}!`);
      invalidar(v.id);
    },
    onError: (e) => erro(e, "Não foi possível transferir."),
  });
  const retirar = useMutation({
    mutationFn: (v: { id: string; redistribuir: boolean }) =>
      api.post<{ novaResponsavel: { nome: string } | null }>(`/conversas/${v.id}/retirar`, {
        redistribuir: v.redistribuir,
      }),
    onSuccess: (r, v) => {
      toast.success(
        r.novaResponsavel
          ? `Passada para ${r.novaResponsavel.nome}!`
          : "Conversa devolvida à fila!",
      );
      invalidar(v.id);
    },
    onError: (e) => erro(e, "Não foi possível devolver à fila."),
  });
  const prioridade = useMutation({
    mutationFn: (v: { id: string; prioridade: number }) =>
      api.post(`/conversas/${v.id}/prioridade`, { prioridade: v.prioridade }),
    onSuccess: (_r, v) => invalidar(v.id),
    onError: (e) => erro(e, "Não foi possível mudar a prioridade."),
  });
  return { assumir, transferir, retirar, prioridade };
}

export function useMudarPresenca() {
  const invalidar = useInvalidarOperacao();
  return useMutation({
    mutationFn: (v: { usuarioId: string; status: StatusPresenca }) =>
      api.patch(`/usuarios/${v.usuarioId}/presenca`, { status: v.status }),
    onSuccess: () => invalidar(),
    onError: (e) => erro(e, "Não foi possível mudar o status."),
  });
}
